import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EuroIcon from '@mui/icons-material/EuroSymbol';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CalendarTodayIcon from '@mui/icons-material/CalendarTodayOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useTranslation } from 'react-i18next';
import type { TimeEntry } from '@altyor-kronos/shared-types';
import {
  buildEntryTimes,
  formatDurationSeconds,
  shiftEntryToDate,
  toDateInput,
  toTimeInput,
} from './timeEntries';
import { colors } from '../../core/theme/theme';
import { useDesignationsFor } from './useDesignationsFor';
import TimeLockGuard from './TimeLockGuard';
import { useTimeLock } from './useTimeLock';
import { useActiveTags } from './useKronosData';
import { dedupeDesignations } from './dedupeDesignations';

/** Minimal project option for the inline project picker. */
export interface TrackerRowProjectOption {
  id: string;
  label: string;
}

export interface TrackerEntryRowProps {
  entry: TimeEntry;
  projectOptions: TrackerRowProjectOption[];
  projectLabel: string;
  designationLabel: string;
  /**
   * Commit a partial Firestore update for THIS entry. Resolves on
   * success; rejects on failure (the row reverts + shows an inline
   * error). The caller adds `updatedAt`.
   */
  onUpdate: (patch: Record<string, unknown>) => Promise<void>;
  onDuplicate: () => void;
  onDelete: () => void;
  onContinueTimer: () => void;
}

/**
 * Clockify-style inline-editable recap row. Used ONLY in the tracker
 * "Today" list. Every field writes independently via `onUpdate`; the
 * read-only `TimeEntryRow` covers the project tab + dashboard.
 *
 * Project + designation are committed as a PAIR: changing the project
 * holds the choice locally and only writes `{ projectId, designationId }`
 * together once a designation for the new project is picked — so
 * Firestore never holds an inconsistent (project, designation) tuple.
 */
export default function TrackerEntryRow({
  entry,
  projectOptions,
  projectLabel,
  designationLabel,
  onUpdate,
  onDuplicate,
  onDelete,
  onContinueTimer,
}: TrackerEntryRowProps) {
  const { t } = useTranslation();
  const id = entry.id;
  const tagOptions = useActiveTags();
  // Le verrou est consulté ICI et pas chez les appelants : cette ligne est
  // rendue par le tracker ET par l'historique, un seul garde couvre les deux.
  const { isMonthClosed } = useTimeLock();

  const startDate = entry.startTime.toDate();
  const endDate = entry.endTime.toDate();
  const dateISO = toDateInput(startDate);

  // ── Local field buffers (resync when the entry changes) ──
  const [description, setDescription] = useState(entry.description ?? '');
  const [tags, setTags] = useState<string[]>(entry.tags ?? []);
  const [startHHmm, setStartHHmm] = useState(toTimeInput(startDate));
  const [endHHmm, setEndHHmm] = useState(toTimeInput(endDate));
  const [timeError, setTimeError] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  useEffect(() => {
    setDescription(entry.description ?? '');
    setTags(entry.tags ?? []);
    setStartHHmm(toTimeInput(entry.startTime.toDate()));
    setEndHHmm(toTimeInput(entry.endTime.toDate()));
    setTimeError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    entry.id,
    entry.description,
    entry.startTime,
    entry.endTime,
    entry.tags,
  ]);

  // ── Project / designation picker ──
  // `pendingProjectId` differs from entry.projectId only while the user
  // is mid-change (project chosen, designation not yet committed).
  const [pendingProjectId, setPendingProjectId] = useState(entry.projectId);
  useEffect(() => setPendingProjectId(entry.projectId), [entry.projectId]);

  const projectDesignations = useDesignationsFor(pendingProjectId);
  const projectChanged = pendingProjectId !== entry.projectId;

  // Defensive dedup: dev seeds historically created duplicate designation
  // docs, which surfaced both as repeated picker rows and a React
  // duplicate-key warning. See dedupeDesignations.ts.
  const designationOptions = useMemo(
    () =>
      dedupeDesignations(projectDesignations.designations).map((d) => ({
        id: d.id,
        label: d.name,
      })),
    [projectDesignations.designations],
  );

  // ── Date popover + ⋮ menu anchors ──
  const [dateAnchor, setDateAnchor] = useState<HTMLElement | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  function reportFailure(revert: () => void) {
    revert();
    setRowError(t('kronos.entries.saveFailed'));
  }

  async function commitDescription() {
    const next = description.trim();
    if (next === (entry.description ?? '')) return;
    setRowError(null);
    try {
      await onUpdate({ description: next || null });
    } catch {
      reportFailure(() => setDescription(entry.description ?? ''));
    }
  }

  async function commitTags() {
    const same =
      tags.length === (entry.tags?.length ?? 0) &&
      tags.every((tg, i) => tg === entry.tags?.[i]);
    if (same) return;
    setRowError(null);
    try {
      await onUpdate({ tags });
    } catch {
      reportFailure(() => setTags(entry.tags ?? []));
    }
  }

  async function commitTimes() {
    const built = buildEntryTimes({
      date: dateISO,
      mode: 'range',
      startHHmm,
      endHHmm,
      durationHours: '',
    });
    if ('error' in built) {
      setTimeError(true);
      return;
    }
    setTimeError(false);
    // No-op if unchanged.
    if (
      built.startTime.getTime() === startDate.getTime() &&
      built.endTime.getTime() === endDate.getTime()
    ) {
      return;
    }
    setRowError(null);
    try {
      await onUpdate({
        startTime: built.startTime,
        endTime: built.endTime,
        durationSeconds: built.durationSeconds,
      });
    } catch {
      reportFailure(() => {
        setStartHHmm(toTimeInput(entry.startTime.toDate()));
        setEndHHmm(toTimeInput(entry.endTime.toDate()));
      });
    }
  }

  async function commitBillable() {
    setRowError(null);
    try {
      await onUpdate({ billable: !entry.billable });
    } catch {
      setRowError(t('kronos.entries.saveFailed'));
    }
  }

  async function commitDate(newDateISO: string) {
    setDateAnchor(null);
    const shifted = shiftEntryToDate(startDate, endDate, newDateISO);
    if (!shifted) return;
    setRowError(null);
    try {
      await onUpdate({ startTime: shifted.startTime, endTime: shifted.endTime });
    } catch {
      setRowError(t('kronos.entries.saveFailed'));
    }
  }

  function handleProjectChange(option: TrackerRowProjectOption | null) {
    if (!option) return;
    setPendingProjectId(option.id);
    projectDesignations.load();
  }

  async function handleDesignationChange(
    option: { id: string; label: string } | null,
  ) {
    if (!option) return;
    setRowError(null);
    try {
      if (projectChanged) {
        // Commit project + designation as a pair.
        await onUpdate({ projectId: pendingProjectId, designationId: option.id });
      } else if (option.id !== entry.designationId) {
        await onUpdate({ designationId: option.id });
      }
    } catch {
      reportFailure(() => setPendingProjectId(entry.projectId));
    }
  }

  // While the project is unchanged the picker shows the resolved label;
  // mid-change it has no committed designation yet (forces a pick).
  const currentProjectOption =
    projectOptions.find((p) => p.id === pendingProjectId) ??
    { id: pendingProjectId, label: projectLabel };
  const currentDesignationOption = projectChanged
    ? null
    : { id: entry.designationId, label: designationLabel };

  return (
    <TimeLockGuard closed={isMonthClosed(startDate)}>
    <Box
      data-testid={`tracker-entry-row-${id}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'minmax(160px,1.4fr) minmax(220px,1.6fr) minmax(110px,0.8fr) auto auto 64px auto auto auto',
        },
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderTop: `1px solid ${colors.lightGray}`,
      }}
    >
      {/* Description */}
      <TextField
        variant="standard"
        size="small"
        fullWidth
        placeholder={t('kronos.tracker.descriptionPlaceholder')}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onBlur={commitDescription}
        slotProps={{ input: { disableUnderline: true } }}
        data-testid={`tracker-entry-description-${id}`}
      />

      {/* Project · Designation */}
      <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
        <Autocomplete
          size="small"
          options={projectOptions}
          value={currentProjectOption}
          onChange={(_, v) => handleProjectChange(v)}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          disableClearable
          sx={{ flex: 1, minWidth: 100 }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder={t('kronos.tracker.project')}
              data-testid={`tracker-entry-project-${id}`}
            />
          )}
        />
        <Autocomplete
          size="small"
          options={designationOptions}
          value={currentDesignationOption}
          onOpen={projectDesignations.load}
          onChange={(_, v) => handleDesignationChange(v)}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          loading={projectDesignations.loading}
          sx={{ flex: 1, minWidth: 100 }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder={t('kronos.tracker.designation')}
              error={projectChanged}
              data-testid={`tracker-entry-designation-${id}`}
            />
          )}
        />
      </Box>

      {/* Tags */}
      <Autocomplete
        multiple
        size="small"
        options={[...new Set([...tagOptions, ...tags])]}
        value={tags}
        onChange={(_, v) => setTags(v as string[])}
        onBlur={commitTags}
        sx={{ minWidth: 0 }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            placeholder={t('kronos.tracker.tagsPlaceholder')}
            data-testid={`tracker-entry-tags-${id}`}
          />
        )}
      />

      {/* Start — End */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TextField
          type="time"
          variant="standard"
          value={startHHmm}
          onChange={(e) => setStartHHmm(e.target.value)}
          onBlur={commitTimes}
          error={timeError}
          slotProps={{
            input: { disableUnderline: true },
            htmlInput: { 'data-testid': `tracker-entry-start-${id}` },
          }}
          sx={{ width: 92, '& input': { px: 0 } }}
        />
        <Typography variant="body2" color="text.secondary">–</Typography>
        <TextField
          type="time"
          variant="standard"
          value={endHHmm}
          onChange={(e) => setEndHHmm(e.target.value)}
          onBlur={commitTimes}
          error={timeError}
          slotProps={{
            input: { disableUnderline: true },
            htmlInput: { 'data-testid': `tracker-entry-end-${id}` },
          }}
          sx={{ width: 92, '& input': { px: 0 } }}
        />
      </Box>

      {/* Date */}
      <Box>
        <Tooltip title={t('kronos.entries.changeDate')}>
          <IconButton
            size="small"
            onClick={(e) => setDateAnchor(e.currentTarget)}
            aria-label={t('kronos.entries.changeDate')}
            data-testid={`tracker-entry-date-${id}`}
          >
            <CalendarTodayIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Popover
          open={Boolean(dateAnchor)}
          anchorEl={dateAnchor}
          onClose={() => setDateAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 1.5 }}>
            <TextField
              type="date"
              size="small"
              defaultValue={dateISO}
              onChange={(e) => e.target.value && commitDate(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { 'data-testid': `tracker-entry-date-input-${id}` },
              }}
            />
          </Box>
        </Popover>
      </Box>

      {/* Duration (read-only computed) */}
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}
        data-testid={`tracker-entry-duration-${id}`}
      >
        {formatDurationSeconds(entry.durationSeconds)}
      </Typography>

      {/* Billable toggle */}
      <Tooltip title={t('kronos.entries.billableTooltip')}>
        <IconButton
          size="small"
          onClick={commitBillable}
          aria-label={t('kronos.entries.billableTooltip')}
          aria-pressed={entry.billable}
          data-testid={`tracker-entry-billable-${id}`}
          sx={{ color: entry.billable ? 'primary.main' : colors.textMuted }}
        >
          <EuroIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      {/* Continue timer */}
      <Tooltip title={t('kronos.entries.continueTimer')}>
        <IconButton
          size="small"
          onClick={onContinueTimer}
          aria-label={t('kronos.entries.continueTimer')}
          data-testid={`tracker-entry-continue-${id}`}
        >
          <PlayArrowIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* ⋮ menu */}
      <Box>
        <IconButton
          size="small"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label={t('kronos.entries.moreActions')}
          data-testid={`tracker-entry-menu-${id}`}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onDuplicate();
            }}
            data-testid={`tracker-entry-duplicate-${id}`}
          >
            <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
            {t('kronos.entries.duplicate')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onDelete();
            }}
            data-testid={`tracker-entry-delete-${id}`}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            {t('kronos.entries.delete')}
          </MenuItem>
        </Menu>
      </Box>

      {rowError && (
        <Typography
          variant="caption"
          color="error"
          sx={{ gridColumn: '1 / -1' }}
          data-testid={`tracker-entry-error-${id}`}
        >
          {rowError}
        </Typography>
      )}
    </Box>
    </TimeLockGuard>
  );
}
